import React, { useState } from 'react';
import { Animated, Keyboard, Platform, StyleSheet, View } from 'react-native';
import showAlert from 'src/core/utils/showAlert';
import useShake from 'src/presentation/hooks/useShake';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import FormField from 'src/presentation/components/atoms/FormField';
import PasswordField from 'src/presentation/components/atoms/PasswordField';
import SubmitButton from 'src/presentation/components/atoms/SubmitButton';
import Translations from 'src/core/utils/Translations';
import { useAuthenticationStore } from 'src/domain/stores/useAuthenticationStore';
import Dimens from 'src/core/utils/Dimens';

interface RegisterFormProps {
	onRegister: (route: string) => void;
	onGoToLogin: () => void;
	onForgotPassword: () => void;
}

function RegisterForm({ onRegister, onGoToLogin, onForgotPassword }: RegisterFormProps) {
	const authStore = useAuthenticationStore();
	const { validator } = useServicesStore();
	const [email, setEmail] = useState<string>('');
	const [password, setPassword] = useState<string>('');
	const [firstname, setFirstname] = useState<string>('');
	const [lastname, setLastname] = useState<string>('');
	const [passwordRepeat, setPasswordRepeat] = useState<string>('');

	const [firstnameError, setFirstnameError] = useState<string>('');
	const [lastnameError, setLastnameError] = useState<string>('');
	const [emailError, setEmailError] = useState<string>('');
	const [passwordError, setPasswordError] = useState<string>('');
	const [passwordRepeatError, setPasswordRepeatError] = useState<string>('');
	const { translateX: buttonShake, shake } = useShake();

	const isIos = Platform.OS === 'ios';

	// Inline validation, top to bottom; returns whether the form is valid.
	const validate = () => {
		const firstnameMsg = !firstname.trim() ? Translations.errorFieldRequired : '';
		const lastnameMsg = !lastname.trim() ? Translations.errorFieldRequired : '';
		const emailMsg = !email.trim()
			? Translations.errorFieldRequired
			: validator.validateEmail({ email })
				? ''
				: Translations.errorInvalidEmailInline;
		const passwordMsg = !password
			? Translations.errorFieldRequired
			: password.length < 8
				? Translations.errorPasswordTooShortMessage
				: '';
		const passwordRepeatMsg = !passwordRepeat
			? Translations.errorFieldRequired
			: password !== passwordRepeat
				? Translations.errorPasswordsNotMatchingInline
				: '';

		setFirstnameError(firstnameMsg);
		setLastnameError(lastnameMsg);
		setEmailError(emailMsg);
		setPasswordError(passwordMsg);
		setPasswordRepeatError(passwordRepeatMsg);

		return !(firstnameMsg || lastnameMsg || emailMsg || passwordMsg || passwordRepeatMsg);
	};

	const onSubmit = async () => {
		Keyboard.dismiss();

		if (!validate()) {
			shake();
			return;
		}

		const response = await authStore.register(email, password, firstname, lastname);

		if (!response.success) {
			const message = response.message ?? '';
			// Backend rejects a duplicate email.
			if (/bestaat|al geregistreerd|already|exists|in gebruik/i.test(message)) {
				showAlert({
					title: Translations.errorEmailExistsTitle,
					message: Translations.errorEmailExistsMessage,
					buttons: [
						{ text: Translations.buttonCancel, style: 'cancel' },
						{ text: Translations.buttonForgotPassword, onPress: onForgotPassword },
						{ text: Translations.buttonLogin, onPress: onGoToLogin },
					],
				});
				return;
			}
			authStore.handleErrorResponse(message);
			return;
		}

		authStore.handleSuccessResponse(Translations.successRegisterSubject, Translations.successRegisterMessage, () =>
			onRegister('Index'),
		);
	};

	return (
		<View>
			<FormField
				error={firstnameError}
				label={Translations.labelFirstName}
				placeholder={isIos ? Translations.placeholderFirstName : Translations.placeholderFirstNameShort}
				value={firstname}
				onChangeText={(text) => {
					setFirstname(text);
					if (firstnameError) setFirstnameError('');
				}}
			/>
			<FormField
				error={lastnameError}
				label={Translations.labelLastName}
				placeholder={isIos ? Translations.placeholderLastName : Translations.placeholderLastNameShort}
				value={lastname}
				onChangeText={(text) => {
					setLastname(text);
					if (lastnameError) setLastnameError('');
				}}
			/>
			<FormField
				error={emailError}
				keyboardType="email-address"
				label={Translations.labelEmail}
				placeholder={isIos ? Translations.placeholderEmail : Translations.placeholderEmailAndroid}
				value={email}
				onChangeText={(text) => {
					setEmail(text);
					if (emailError) setEmailError('');
				}}
			/>
			<PasswordField
				error={passwordError}
				label={Translations.labelPassword}
				placeholder={isIos ? Translations.placeholderPasswordMinChars : Translations.placeholderPasswordShort}
				value={password}
				onChangeText={(text) => {
					setPassword(text);
					if (passwordError) setPasswordError('');
				}}
			/>
			<PasswordField
				error={passwordRepeatError}
				label={Translations.labelPasswordRepeat}
				placeholder={isIos ? Translations.placeholderPasswordRepeat : Translations.placeholderPasswordRepeatShort}
				value={passwordRepeat}
				onChangeText={(text) => {
					setPasswordRepeat(text);
					if (passwordRepeatError) setPasswordRepeatError('');
				}}
			/>

			<Animated.View style={{ transform: [{ translateX: buttonShake }] }}>
				<SubmitButton
					isLoading={authStore.isLoading}
					label={Translations.buttonRegister}
					style={isIos ? undefined : styles.submitButton}
					onPress={onSubmit}
				/>
			</Animated.View>
		</View>
	);
}

export default RegisterForm;

const styles = StyleSheet.create({
	submitButton: {
		marginTop: Dimens.margin40,
		flexDirection: 'row',
		justifyContent: 'center',
	},
});
